import React, { useCallback, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import { Loader } from '@els/els-react--loader';
import { tgitems } from '@Constants';
import UserDatabases from '@Services/UserDatabases';
import ResultsList from '@Components/Results/ResultsList';
import { ResultsMap } from '@Containers/ResultsPreview/contexts';
import BlockContextColor from '@Containers/ResultsPreview/Components/BlockContextColor';
import BlockLabel from '@Containers/ResultsPreview/Components/BlockLabel';
import BlockFeedbacks from '@Containers/ResultsPreview/Components/BlockFeedbacks';
import BlockActionButtons from '@Containers/ResultsPreview/Components/BlockActionButtons';
import { useHistory } from 'react-router-dom';

function GroupSingleDatabase({
  previewItem,
  previewItemIndex,
  showMoreResults,
  bioactivityVisualizationButtonEnabled,
  showStructurePopup,
  copyQuery,
  createAlert,
  redirectToResults,
  cancelQueriesInGroup,
}) {
  const showDatabaseLabel = !UserDatabases.isRCSDatabase(previewItem.databaseId) && previewItem.fullyLoaded;
  const showProgressLoader = !previewItem.fullyLoaded && !get(previewItem, 'query.cancelled');
  const showQueryCancelled = get(previewItem, 'query.cancelled');
  const showViewResults = previewItem.fullyLoaded && previewItem.resultsCount > 0;
  const showPredictionButton = previewItem.fullyLoaded && !get(previewItem, 'query.cancelled');
  const showBioactivityVisualizationButton =
    bioactivityVisualizationButtonEnabled &&
    previewItem.context === tgitems &&
    previewItem.fullyLoaded &&
    previewItem.resultName &&
    !get(previewItem, 'query.cancelled');

  const [expanded, setExpanded] = useState(false);
  const history = useHistory();
  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  useEffect(() => {
    if (expanded) {
      showMoreResults(previewItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const resultsMap = useContext(ResultsMap);
  const results = resultsMap[previewItem.queryId];
  const iconName = get(previewItem, 'title', '').toLowerCase().replace(' ', '-');

  return (
    <div
      className={cn('quick-results-preview__item e2e-quick-results-item', {
        'quick-results-preview__item-loading': !previewItem.fullyLoaded,
      })}>
      <BlockContextColor previewItem={previewItem} showIcon />
      <div className="quick-results-preview__item_wrapper">
        <div className="quick-results-preview__item_controls">
          <BlockLabel previewItem={previewItem} showResultsCount showTitle showDatabaseLabel={showDatabaseLabel} />
          <BlockActionButtons
            previewItem={previewItem}
            showProgressLoader={showProgressLoader}
            onCancelClick={() => cancelQueriesInGroup(previewItem.groupId)}
            showQueryCancelled={showQueryCancelled}
            showExpandButton={showViewResults}
            expandButtonConfig={{
              label: 'Preview Results',
              e2eName: 'preview-results',
            }}
            expandButtonAttrs={{
              'aria-label': `Preview results from ${previewItem.databaseLabel}`,
            }}
            expanded={expanded}
            toggleExpanded={toggleExpanded}
            showViewResultsButton={showViewResults}
            onViewResultsButtonClick={() => redirectToResults(previewItem, history, previewItemIndex)}
            showPredictionButton={showPredictionButton}
            showBioactivityVisualizationButton={showBioactivityVisualizationButton}
          />
          <div className="quick-results-preview__item_controls_wrapper">
            <BlockFeedbacks
              previewItem={previewItem}
              copyQuery={copyQuery}
              createAlert={createAlert}
              showStructurePopup={showStructurePopup}
              showFeedbacks
            />
          </div>
        </div>

        {expanded && (
          <div
            id={previewItem.resultName}
            className={`quick-results-preview__item_preview u-els-margin-top quick-results-preview__item_preview-${iconName}`}>
            <div className="quick-results-preview__item_preview_header">
              <span>Top 3 results</span>
            </div>

            {results ? (
              <ResultsList
                metadata={previewItem}
                context={previewItem.context}
                results={results}
                size={results.length}
                hideCheckbox
              />
            ) : (
              <Loader isSmall isBlocking={false} className="results-preview__progress-loader" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

GroupSingleDatabase.propTypes = {
  previewItem: PropTypes.object.isRequired,
  previewItemIndex: PropTypes.number,
  showMoreResults: PropTypes.func.isRequired,
  bioactivityVisualizationButtonEnabled: PropTypes.bool,
  showStructurePopup: PropTypes.func.isRequired,
  copyQuery: PropTypes.func.isRequired,
  createAlert: PropTypes.func.isRequired,
  redirectToResults: PropTypes.func.isRequired,
  cancelQueriesInGroup: PropTypes.func.isRequired,
};

export default GroupSingleDatabase;
